import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Heart, Users, Award, Globe, Church } from 'lucide-react';
import { SacredSection, ChristianIcon, ScriptureQuote } from '../components/ChristianElements';

const Story: React.FC = () => {
  const { t } = useTranslation();

  const values = [
    {
      icon: Heart,
      title: t('story.values.reverence.title'),
      description: t('story.values.reverence.description'),
      christianIcon: 'cross'
    },
    {
      icon: Users,
      title: t('story.values.family.title'),
      description: t('story.values.family.description'),
      christianIcon: 'dove'
    },
    {
      icon: Award,
      title: t('story.values.excellence.title'),
      description: t('story.values.excellence.description'),
      christianIcon: 'vine'
    },
    {
      icon: Globe,
      title: t('story.values.sacredConnection.title'),
      description: t('story.values.sacredConnection.description'),
      christianIcon: 'olive'
    },
    {
      icon: Church,
      title: t('story.values.communitySupport.title'),
      description: t('story.values.communitySupport.description'),
      christianIcon: 'church'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('meta.story.title')}</title>
        <meta 
          name="description" 
          content={t('meta.story.description')} 
        />
        <link rel="canonical" href={t('meta.story.canonical')} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 md:pt-24 pb-12 md:pb-16 bg-primary-50 holy-land-bg">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center px-4">
            <div className="mb-4 md:mb-6">
              <ChristianIcon icon="cross" size="lg" className="mx-auto text-gold-600" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-scripture font-bold text-primary-800 mb-4 md:mb-6">
              {t('story.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 px-2">
              {t('story.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Story */}
      <SacredSection className="section-padding bg-white" title={t('story.sections.sacredMission')}>
        <div className="container-max">
          <div className="max-w-4xl mx-auto px-4">
            <div className="mb-6 md:mb-8">
              <ChristianIcon icon="dove" size="lg" className="mx-auto mb-4" />
            </div>
            <div className="prose prose-base md:prose-lg mx-auto">
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 md:mb-8">
                {t('story.content')}
              </p>
              
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 md:mb-8">
                {t('story.mission')}
              </p>
              
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 md:mb-8">
                {t('story.support')}
              </p>
            </div>
            
            {/* Scripture quote */}
            <div className="mt-6 md:mt-8">
              <ScriptureQuote 
                verse={t('scriptures.love.verse')}
                reference={t('scriptures.love.reference')}
                className="max-w-2xl mx-auto"
              />
            </div>
          </div>
        </div>
      </SacredSection>

      {/* Values Section */}
      <SacredSection className="section-padding bg-primary-50" title={t('story.sections.values')}>
        <div className="container-max">
          <div className="text-center mb-8 md:mb-12">
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              {t('story.descriptions.valuesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 lg:flex lg:flex-wrap lg:justify-center lg:gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center sacred-border p-4 md:p-6 bg-white/50 w-full sm:w-full lg:w-1/3 lg:max-w-[300px] min-h-[200px] flex flex-col">
                <div className="relative mb-3 md:mb-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto">
                    <value.icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                    <ChristianIcon icon={value.christianIcon as any} size="sm" />
                  </div>
                </div>
                <h3 className="text-lg md:text-xl font-scripture font-semibold text-primary-800 mb-2">
                  {value.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 px-2">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </SacredSection>

      {/* History Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-primary-800 mb-4 md:mb-6">
                {t('story.history.title')}
              </h2>
              <div className="space-y-3 md:space-y-4 text-sm md:text-base text-gray-600">
                {(t('story.history.paragraphs', { returnObjects: true }) as string[]).map((paragraph, index) => (
                  <p key={index}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            
            <div className="bg-primary-50 rounded-lg p-6 md:p-8 mt-6 lg:mt-0">
              <div className="aspect-square bg-gradient-to-br from-primary-100 to-olive-100 rounded-lg overflow-hidden">
                <img 
                  src="/images/finalcoffin.jpg" 
                  alt="Handcrafted coffin from the Holy Land"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Story;
